import React from 'react';
import { Sparkles, Copy, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface AiBoostResponse {
  hookStrength: number;
  brandAlignment: number;
  ctaEffectiveness: number;
  platformOptimization: number;
  contentQuality: number;
  overallScore: number;
  originalScore: {
    hookStrength: number;
    brandAlignment: number;
    ctaEffectiveness: number;
    platformOptimization: number;
    contentQuality: number;
    overallScore: number;
  };
  feedback: string;
  enhancedContent: string;
  enhancedScores: {
    hookStrength: number;
    brandAlignment: number;
    ctaEffectiveness: number;
    platformOptimization: number;
    contentQuality: number;
    overallScore: number;
  };
  reasoning: string;
  originalContent: string;
  brandId: string;
  platforms: string[];
  language: string;
  timestamp: string;
}

interface PostAIReviewCardProps {
  aiResponse: AiBoostResponse | null;
  copied: boolean;
  onCopyContent: () => void;
  onApplyAiContent: () => void;
}

export const PostAIReviewCard: React.FC<PostAIReviewCardProps> = ({
  aiResponse,
  copied,
  onCopyContent,
  onApplyAiContent
}) => {
  const { t } = useTranslation();

  const formatFeedbackText = (text: string) => {
    // Split text into lines
    const lines = text.split('\n');
    const formattedElements: JSX.Element[] = [];
    
    lines.forEach((line, index) => {
      if (line.trim() === '') {
        // Skip empty lines
        return;
      }
      
      // Check if line starts with "* **" (bullet point with bold title)
      if (line.trim().startsWith('* **')) {
        // Extract the bold part and the rest
        const match = line.match(/^\* \*\*(.*?)\*\*:\s*(.*)/);
        if (match) {
          const [, title, content] = match;
          formattedElements.push(
            <div key={index} className="mt-3 first:mt-0">
              <p className="text-sm text-gray-700">
                <span className="font-bold text-gray-900">• {title}:</span> {content}
              </p>
            </div>
          );
        } else {
          // Fallback if regex doesn't match
          formattedElements.push(
            <p key={index} className="text-sm text-gray-700 mt-3 first:mt-0">
              {line}
            </p>
          );
        }
      } else {
        // Regular line - check for **bold** text within it
        const parts = line.split(/(\*\*.*?\*\*)/);
        const formattedParts = parts.map((part, partIndex) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            // Bold text
            const boldText = part.slice(2, -2);
            return <span key={partIndex} className="font-bold text-gray-900">{boldText}</span>;
          }
          return part;
        });
        
        formattedElements.push(
          <p key={index} className="text-sm text-gray-700 mt-3 first:mt-0">
            {formattedParts}
          </p>
        );
      }
    });
    
    return formattedElements;
  };

  return (
    <div className="bg-white rounded-xl border border-black shadow-lg p-4 sm:p-6 space-y-4">
      <div className="flex items-center gap-3 mb-6">
        <Sparkles className="w-6 h-6 text-purple-600" />
        <h2 className="text-lg font-semibold text-gray-900">
          {t('aiAnalysis.title', 'AI Post Analysis')}
        </h2>
      </div>

      {/* AI Enhanced Content Section */}
      <div className="border border-green-200 rounded-lg bg-green-50 overflow-hidden">
        <div className="bg-green-100 px-4 py-2 border-b border-green-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-green-800">{t('postContent.aiEnhancedContent', 'AI Enhanced Content')}</h3>
            {aiResponse && (
              <div className="flex items-center gap-2">
                <button 
                  onClick={onCopyContent}
                  className="inline-flex items-center p-1 text-gray-600 hover:text-green-700 rounded"
                  title="Copy to clipboard"
                >
                  {copied ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </button>
                <button 
                  onClick={onApplyAiContent}
                  className="inline-flex items-center px-2 py-1 text-xs text-white bg-primary-600 rounded hover:bg-primary-700"
                >
                  {t('cards.aiContent.apply', 'Apply')}
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="p-4">
          {aiResponse ? (
            <p className="text-sm text-gray-800">{aiResponse.enhancedContent}</p>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <Sparkles className="w-6 h-6 text-green-400 mb-2" />
              <p className="text-sm font-medium text-gray-600">{t('postContent.useBoostPrompt', 'Use "Boost with AI" to enhance your content')}</p>
              <p className="text-xs text-gray-500 mt-1">{t('postContent.optimizationDescription', 'Our AI will optimize your caption for better engagement')}</p>
            </div>
          )}
        </div>
      </div>
      
      {/* AI Feedback Section */}
      <div className="border border-purple-200 rounded-lg bg-purple-50 overflow-hidden">
        <div className="bg-purple-100 px-4 py-2 border-b border-purple-200">
          <h3 className="text-sm font-medium text-purple-800">{t('postContent.aiFeedback', 'AI Feedback')}</h3>
        </div>
        <div className="p-4">
          {aiResponse ? (
            <div className="space-y-3">
              {formatFeedbackText(aiResponse.feedback)}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <Sparkles className="w-6 h-6 text-purple-400 mb-2" />
              <p className="text-sm font-medium text-gray-600">{t('postContent.feedbackWillAppearHere', 'AI feedback will appear here')}</p>
              <p className="text-xs text-gray-500 mt-1">{t('postContent.professionalInsights', 'Get professional insights on how to improve your content')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Scores Section */}
      {aiResponse && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="border border-orange-200 rounded-lg bg-orange-50 overflow-hidden">
              <div className="bg-orange-100 px-4 py-2 border-b border-orange-200">
                <h3 className="text-sm font-medium text-orange-800">{t('cards.aiContent.originalScore', 'Original Score')}</h3>
              </div>
              <div className="p-4 flex items-center justify-center">
                <span className="text-3xl font-bold text-orange-600">{aiResponse.originalScore.overallScore.toFixed(1)}/10</span>
              </div>
            </div>
            
            <div className="border border-green-200 rounded-lg bg-green-50 overflow-hidden">
              <div className="bg-green-100 px-4 py-2 border-b border-green-200">
                <h3 className="text-sm font-medium text-green-800">{t('cards.aiContent.enhancedScore', 'Enhanced Score')}</h3>
              </div>
              <div className="p-4 flex items-center justify-center">
                <span className="text-3xl font-bold text-green-600">{aiResponse.enhancedScores.overallScore.toFixed(1)}/10</span>
              </div>
            </div>
          </div>
          
          {/* Detailed Scores */}
          <div className="border border-gray-200 rounded-lg bg-gray-50 overflow-hidden">
            <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-800">{t('cards.aiContent.detailedScores', 'Detailed Analysis')}</h3>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-700">{t('cards.aiContent.hookStrength', 'Hook Strength')}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-orange-600">{aiResponse.originalScore.hookStrength}/10</span>
                  <span className="text-gray-400">→</span>
                  <span className="text-sm font-medium text-green-600">{aiResponse.enhancedScores.hookStrength}/10</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-700">{t('cards.aiContent.brandAlignment', 'Brand Alignment')}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-orange-600">{aiResponse.originalScore.brandAlignment}/10</span>
                  <span className="text-gray-400">→</span>
                  <span className="text-sm font-medium text-green-600">{aiResponse.enhancedScores.brandAlignment}/10</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-700">{t('cards.aiContent.ctaEffectiveness', 'CTA Effectiveness')}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-orange-600">{aiResponse.originalScore.ctaEffectiveness}/10</span>
                  <span className="text-gray-400">→</span>
                  <span className="text-sm font-medium text-green-600">{aiResponse.enhancedScores.ctaEffectiveness}/10</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-700">{t('cards.aiContent.platformOptimization', 'Platform Optimization')}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-orange-600">{aiResponse.originalScore.platformOptimization}/10</span>
                  <span className="text-gray-400">→</span>
                  <span className="text-sm font-medium text-green-600">{aiResponse.enhancedScores.platformOptimization}/10</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-700">{t('cards.aiContent.contentQuality', 'Content Quality')}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-orange-600">{aiResponse.originalScore.contentQuality}/10</span>
                  <span className="text-gray-400">→</span>
                  <span className="text-sm font-medium text-green-600">{aiResponse.enhancedScores.contentQuality}/10</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 